/*
 * NOC-INT/CC – Director’s Compliance Checklist, as written by the director in October 2026.
 * The office fills in Next due, Done and Notes; the other columns are the checklist itself.
 */

export const DIRECTOR_COMPLIANCE_CHECKLIST_KEY = 'director-compliance-checklist';

const row = (key, item, check, frequency, who) => ({ key, item, check, frequency, who });

export const DIRECTOR_COMPLIANCE_CHECKLIST_SECTIONS = Object.freeze([
	{
		key: 'company-legal',
		title: '1. Company and Legal',
		rows: [
			row('confirmation-statement', 'Confirmation statement', 'Filed with Companies House. Company details correct.', 'Every 12 months', 'Director'),
			row('director-identity-verification', 'Director identity verification', 'Companies House personal code given with the confirmation statement.', 'Once', 'Director'),
			row('annual-accounts', 'Annual accounts and corporation tax', 'Filed and paid on time.', 'Yearly', 'Accountant'),
			row('vat-returns', 'VAT returns', 'Filed and paid on time.', 'Each VAT period', 'Accountant'),
			row('payroll-year-end', 'Payroll year end', 'P60 given to each employee by 31 May.', 'Yearly', 'Accountant'),
			row('waste-carrier-registration', 'Waste carrier registration', 'Needed if the Company carries waste away from sites. Lower tier: no renewal. Upper tier: renewed every 3 years.', 'Check once', 'Director'),
		],
	},
	{
		key: 'insurance',
		title: '2. Insurance',
		rows: [
			row('employers-liability', 'Employers’ liability', 'Current. Cover of at least £5 million. Certificate in the mobile app.', 'Yearly, at renewal', 'Director'),
			row('public-liability', 'Public liability', 'Current. Cover meets the level clients’ contracts require.', 'Yearly, at renewal', 'Director'),
			row('other-cover', 'Other cover', 'Any other cover held, such as fidelity, keys, professional indemnity, equipment or business interruption, as the policy schedule shows.', 'Yearly, at renewal', 'Director'),
			row('subcontractors-insurance', 'Subcontractors’ insurance', 'Each subcontractor’s public liability certificate on file and in date.', 'At onboarding and on expiry', 'Office'),
			row('client-insurance-requirements', 'Client requirements', 'Insurance levels required by each client contract are met.', 'New contract or renewal', 'Director'),
		],
	},
	{
		key: 'memberships',
		title: '3. Memberships and Accreditations',
		rows: [
			row('bicsc-membership', 'BICSc Corporate Membership', 'Renewed.', 'Yearly', 'Director'),
			row('ssip-accreditation', 'SSIP accreditation', 'Certificate current, for example SMAS Worksafe, CHAS or SafeContractor. Reassessment submitted before expiry.', 'Yearly, when held', 'Director'),
			row('iso-certification', 'ISO certification', 'Not required by law. Asked for by some large tenders: ISO 9001 quality, ISO 14001 environment, ISO 45001 health and safety. If held: surveillance audit yearly, recertification every 3 years.', 'Optional', 'Director'),
		],
	},
	{
		key: 'director-certificates',
		title: '4. Competent Person – Director’s Certificates',
		rows: [
			row('bicsc-licence', 'BICSc Licence to Practice', 'Current.', 'As the certificate shows', 'Director'),
			row('hst-risk-assessment', 'HST Risk Assessment', 'Certificate on file. Refresher as the course provider recommends.', 'As the certificate shows', 'Director'),
			row('hst-coshh', 'HST COSHH', 'Certificate on file. Refresher as the course provider recommends.', 'As the certificate shows', 'Director'),
			row('training-cpd', 'Other training and CPD', 'Courses and reading recorded. Knowledge of health and safety law and guidance kept up to date.', 'Yearly', 'Director'),
		],
	},
	{
		key: 'data-protection',
		title: '5. Data Protection',
		rows: [
			row('ico-fee', 'ICO data protection fee', 'Paid, unless the ICO self-assessment shows the Company is exempt.', 'Yearly', 'Director'),
			row('privacy-notices', 'Privacy notices', 'Notices for employees, subcontractors and the website up to date.', 'Yearly', 'Director'),
			row('confidential-records', 'Confidential records', 'Individual assessments, incident reports and health information kept securely. Records past their keeping period deleted.', 'Yearly', 'Office'),
		],
	},
	{
		key: 'people',
		title: '6. People',
		rows: [
			row('right-to-work', 'Right to work', 'Checked before work starts for every employee and, from 1 October 2026, every individual subcontractor. Repeated before any time-limited permission ends.', 'Before start and on expiry', 'Office'),
			row('written-statement', 'Written statement', 'Given to each employee on or before the first day of work.', 'Each new employee', 'Director'),
			row('pay-rates', 'Pay rates', 'National Minimum Wage and National Living Wage rates applied from 1 April.', 'Every April', 'Accountant'),
			row('workplace-pension', 'Workplace pension', 'Eligible employees enrolled. Re-enrolment and re-declaration of compliance every 3 years.', 'Every 3 years', 'Accountant'),
			row('subcontractor-agreements', 'Subcontractor agreements', 'Agreements current. Status and National Insurance position reviewed.', 'Yearly', 'Director'),
			row('emergency-contacts', 'Emergency contacts', 'Up to date for each employee.', 'Yearly', 'Office'),
			row('home-working-dse', 'Home working – display screen equipment', 'Each employee who uses a computer at home daily, for an hour or more at a time, reads HSE leaflet INDG36 and completes NOC-Display Screen Equipment Workstation Checklist. The Director checks it. Repeated when the workstation or the way it is used changes, or when pain or discomfort is reported. Completed checklists kept on file.', 'New user, on change or on complaint', 'Director'),
		],
	},
	{
		key: 'clients-sites',
		title: '7. Clients and Sites',
		rows: [
			row('client-contracts', 'Client contracts', 'Renewal dates, scope and required insurance levels checked.', 'Yearly and at renewal', 'Director'),
			row('site-information', 'Site information', 'Access, fire arrangements and site hazards received from each client and up to date.', 'New site and on change', 'Office'),
		],
	},
	{
		key: 'tender-policies',
		title: '8. Tender Policies',
		intro: 'Company policies asked for in tenders, kept outside the Employee Health & Safety Pack (Common Assessment Standard).',
		rows: [
			row('environmental-management-policy', 'Environmental Management Policy', 'Reviewed, signed and dated every August. Environmental objectives for the coming year set, and those set the year before checked (policy §3.11 “Monitoring and Review”). Objectives, what went well in the year, and any environmental complaints and incidents recorded in NOC-Environmental Records. From the first review, changes to the policy recorded in a Document Review table at its end.', 'Yearly, in August', 'Director'),
			row('environmental-records', 'Environmental Records', 'Incident and complaint register checked: each entry has the action taken and is closed. At the August review, one line entered in section 1 of the records: objectives for the coming year and what was achieved in the year. Where there were no environmental complaints or incidents, this is stated.', 'Yearly, in August', 'Director'),
			row('equality-diversity-policy', 'Equality and diversity policy', 'Written and reviewed.', 'Yearly, if tenders ask', 'Director'),
			row('quality-policy', 'Quality policy', 'Written and reviewed.', 'Yearly, if tenders ask', 'Director'),
		],
	},
	{
		key: 'health-safety-review',
		title: '9. Health and Safety – Annual Review',
		intro: 'The annual review is required by the Health & Safety Policy Statement (NOC-002/PS §3.13). The date is entered in the index (NOC-E001/IDX §3).',
		rows: [
			row('health-safety-pack', 'Employee Health & Safety Pack', 'All documents reviewed and still valid. One line entered in the index.', 'Yearly', 'Director'),
			row('risk-assessments', 'Risk assessments', 'Still valid for the work carried out. Any new service or method assessed.', 'Yearly and on change', 'Director'),
			row('products', 'Products', 'COSHH-A pages and safety data sheets current in the app. Products labelled and in date.', 'Yearly and for each new product', 'Director'),
			row('training', 'Training', 'Inductions and annual refreshers entered on the Training Matrix (NOC-E025/TM).', 'Yearly', 'Office'),
			row('incidents-near-misses', 'Incidents and near misses', 'Each one investigated. RIDDOR reports made on time. Findings used to update assessments (NOC-024/IR).', 'Yearly', 'Director'),
			row('individual-assessments', 'Individual assessments', 'Each one reviewed (NOC-E023/IRA).', 'Yearly and on change', 'Director'),
			row('ppe', 'PPE', 'Stock, sizes and CE or UKCA marking checked (NOC-E004/PPE).', 'Yearly', 'Office'),
			row('first-aid', 'First aid', 'Personal kits stocked and in date. Sterile eye wash in date (NOC-E006/FA).', 'Routine site inspections', 'Office'),
			row('lone-working', 'Lone working', 'Clocking in and out and contact arrangements working (NOC-E003/LW).', 'Yearly', 'Director'),
			row('concerns-raised', 'Concerns raised', 'Concerns raised by operatives recorded and acted on (NOC-002/PS §3.12).', 'Yearly', 'Director'),
			row('site-inspections', 'Site inspections', 'Routine quality inspection of each site with a regular service, and more often where the client’s contract asks. Further inspections where needed, such as after a complaint or an incident. Findings recorded in NOC-Site Inspection Record (NOC-002/PS §2.2).', 'At least every 3\u00a0months', 'Director'),
			row('new-employees', 'New employees', 'HSE law leaflet given. Employers’ liability certificate in the app (NOC-E021/EIP).', 'Each new employee', 'Office'),
			row('subcontractors-competence', 'Subcontractors’ competence', 'Evidence of competence on file.', 'At onboarding and yearly', 'Office'),
			row('law-guidance', 'Law and guidance', 'Changes to health and safety law and HSE guidance checked.', 'Yearly', 'Director'),
		],
	},
	{
		key: 'equipment',
		title: '10. Equipment',
		rows: [
			row('step-stools-stepladders', 'Step stools and stepladders', 'Inspected by the Company at suitable intervals and after any damage or repair. Result recorded in Appendix A (NOC-E008/WAH).', 'Suitable intervals', 'Director'),
			row('portable-electrical-equipment', 'Portable electrical equipment', 'Formal visual inspection of plug, cable and casing. Combined inspection and test only where the type of equipment requires it. Recorded in Appendix A (NOC-002/PS §3.15).', 'Routine site inspections', 'Director'),
			row('defective-equipment', 'Defective equipment', 'Taken out of use, then repaired or replaced.', 'When found', 'Office'),
		],
	},
]);

export const DIRECTOR_COMPLIANCE_CHECKLIST = Object.freeze({
	key: DIRECTOR_COMPLIANCE_CHECKLIST_KEY,
	title: 'Director’s Compliance Checklist',
	reference: 'NOC-INT/CC',
	intro: [
		'Internal document for the management of the Company. It is not part of the Employee Health & Safety Pack.',
		'Enter the date each item is checked and the next due date.',
	],
	sections: DIRECTOR_COMPLIANCE_CHECKLIST_SECTIONS,
	// Version 1 as the director wrote it: reviewed October 2026, next review October 2027.
	firstVersion: {
		reviewedOn: '2026-10-01',
		nextReviewOn: '2027-10-01',
		entries: {
			'bicsc-membership': { nextDue: '2026-10-31' },
			'hst-risk-assessment': { notes: 'Issued 01/04/2026' },
			'hst-coshh': { notes: 'Issued 16/01/2026' },
			// "August 2027": the August review is due by the end of that month.
			'environmental-management-policy': {
				nextDue: '2027-08-31',
				notes: 'Written, V1 (August 2026). Records: NOC-Environmental Records.',
			},
			'environmental-records': { nextDue: '2027-08-31', notes: 'NOC-Environmental Records' },
		},
	},
});
